import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoTexto, useFormulario, Ventana } from '@tallerya/ui';
import { useCambiosProveedor, type ProveedorCreado } from './datos';

// «Nuevo proveedor al vuelo» del Figma (#56): el alta sin salir de lo que se está
// capturando, con solo el nombre. El código lo asigna el correlativo, y el resto
// se completa después en la página de Proveedores. Sigue a `VentanaCategoria.tsx`.
//
// **Es para los gastos de la fase 3** (#143): el combo de proveedor la abre y,
// al crear, deja elegido al nuevo con `onCreado`. Lo que la pantalla de atrás
// tenía capturado no se toca, porque la ventana no navega.

interface Props {
  abierta: boolean;
  onCerrar: () => void;
  onCreado: (proveedor: ProveedorCreado) => void;
  /** Lo que ya se escribió en el combo, para no pedirlo dos veces. */
  nombreInicial?: string;
}

export function VentanaProveedor({ abierta, ...props }: Props) {
  // Montarla al abrir reinicia el campo cada vez.
  return abierta ? <Contenido {...props} /> : null;
}

function Contenido({ onCerrar, onCreado, nombreInicial = '' }: Omit<Props, 'abierta'>) {
  const { t } = useTraduccion();
  const cambios = useCambiosProveedor();

  const form = useFormulario<{ name: string }>({
    inicial: { name: nombreInicial },
    guardar: async (v) => {
      const { creado, problema } = await cambios.crear({
        code: '',
        name: v.name,
        docTypeId: '',
        docIdentifier: '',
        reference: '',
      });
      if (creado) onCreado(creado);
      return problema;
    },
    camposPorCodigo: { 'supplier.nombre_duplicado': 'name' },
  });

  async function crear() {
    if (await form.enviar()) onCerrar();
  }

  return (
    <Ventana
      abierta
      titulo={t('admin.proveedores.vuelo.titulo')}
      info={t('admin.proveedores.vuelo.info')}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={onCerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void crear()}>
            {t('admin.proveedores.vuelo.crear')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.proveedores.formulario.nombre')}
        obligatorio
        ancho="completo"
        autoComplete="off"
      />
    </Ventana>
  );
}
